import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { api } from '../api/axios'

interface Permissao {
  recurso: string
  acao: string
}

interface Usuario {
  id: number
  nome: string
  email: string
  roles: string[]
  permissoes?: Permissao[]
}

interface AuthContextData {
  usuario: Usuario | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, senha: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (recurso: string, acao: string) => boolean
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(() => {
    const saved = sessionStorage.getItem('usuario')
    return saved ? (JSON.parse(saved) as Usuario) : null
  })
  const [isLoading, setIsLoading] = useState(false)

  const login = useCallback(async (email: string, senha: string) => {
    setIsLoading(true)
    try {
      const { data } = await api.post<{
        access_token: string
        usuario: Usuario
      }>('/auth/login', { email, senha })

      sessionStorage.setItem('access_token', data.access_token)
      sessionStorage.setItem('usuario', JSON.stringify(data.usuario))
      setUsuario(data.usuario)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // ignora erros no logout
    } finally {
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('usuario')
      setUsuario(null)
    }
  }, [])

  const hasPermission = useCallback(
    (recurso: string, acao: string) => {
      if (!usuario) return false
      if (usuario.roles.includes('admin')) return true
      return (
        usuario.permissoes?.some(
          (p) => p.recurso === recurso && p.acao === acao
        ) ?? false
      )
    },
    [usuario]
  )

  const hasRole = useCallback(
    (role: string) => {
      return usuario?.roles.includes(role) ?? false
    },
    [usuario]
  )

  return (
    <AuthContext.Provider
      value={{
        usuario,
        isAuthenticated: !!usuario,
        isLoading,
        login,
        logout,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
